/**
 * Companion Image + Audio surface — lab prototype (Phase B1).
 * Not wired into production HostPlaybackPage.
 */
import React, { Component } from "react";
import { createCompanionPresentationController } from "./CompanionPresentation";
import { createCompanionImageAudio, MediaLoopMode } from "./MediaAsset";

var styles = {
  root: {
    position: "relative",
    width: "100%",
    maxWidth: 960,
    margin: "0 auto",
    background: "#111",
    minHeight: 320,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    maxWidth: "100%",
    maxHeight: 420,
    objectFit: "contain",
  },
  controls: {
    display: "flex",
    gap: 8,
    padding: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  meta: {
    color: "#ccc",
    fontSize: 13,
    padding: "0 12px 12px",
    textAlign: "center",
  },
};

class CompanionPresentationSurface extends Component {
  constructor(props) {
    super(props);
    this.audioRef = React.createRef();
    this._controller = null;
    this.state = {
      status: "idle",
      time: 0,
      duration: 0,
    };
    this.handlePlay = this.handlePlay.bind(this);
    this.handlePause = this.handlePause.bind(this);
  }

  componentDidMount() {
    this._setup();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.companion !== this.props.companion) {
      this._teardown();
      this._setup();
    }
  }

  componentWillUnmount() {
    this._teardown();
  }

  _setup() {
    var self = this;
    var companion = createCompanionImageAudio(this.props.companion || {});
    this._controller = createCompanionPresentationController(companion, {
      onEvent: function (name, payload) {
        if (name === "TimeUpdate") {
          self.setState({
            time: payload.currentTime || 0,
            duration: payload.duration || 0,
          });
        } else if (name === "PlaybackStarted") {
          self.setState({ status: "playing" });
        } else if (name === "PlaybackPaused") {
          self.setState({ status: "paused" });
        } else if (name === "PlaybackEnded") {
          self.setState({ status: "ended" });
        } else if (name === "PlaybackError") {
          self.setState({ status: "error" });
        }
        if (self.props.onEvent) self.props.onEvent(name, payload);
      },
    });
    if (this.audioRef.current) {
      this._controller.attachAudio(this.audioRef.current);
    }
    this.setState({ status: "ready" });
  }

  _teardown() {
    if (this._controller) {
      this._controller.destroy();
      this._controller = null;
    }
  }

  handlePlay() {
    if (this._controller) this._controller.play();
  }

  handlePause() {
    if (this._controller) this._controller.pause();
  }

  render() {
    var companion = createCompanionImageAudio(this.props.companion || {});
    var imageUrl = companion.image && companion.image.url;
    var audioUrl = companion.audio && companion.audio.url;
    var bg = companion.background || "#111";

    return (
      <div
        className="tpg-companion-surface"
        style={Object.assign({}, styles.root, { background: bg })}
        data-loop={companion.loopMode || MediaLoopMode.NONE}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={this.props.title || "Companion artwork"}
            style={Object.assign({}, styles.image, {
              objectFit: companion.fit || "contain",
            })}
          />
        ) : (
          <p style={styles.meta}>No companion image</p>
        )}
        {audioUrl ? (
          <audio ref={this.audioRef} src={audioUrl} preload="metadata" />
        ) : null}
        <div style={styles.controls}>
          <button type="button" onClick={this.handlePlay} disabled={!audioUrl}>
            Play
          </button>
          <button type="button" onClick={this.handlePause} disabled={!audioUrl}>
            Pause
          </button>
        </div>
        <p style={styles.meta}>
          Companion prototype · {this.state.status}
          {audioUrl
            ? " · " +
              Math.floor(this.state.time) +
              "s / " +
              Math.floor(this.state.duration || 0) +
              "s"
            : " · image only"}
          {" · loop=" + (companion.loopMode || "none")}
        </p>
      </div>
    );
  }
}

export default CompanionPresentationSurface;
